import { useMemo, useState } from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  STANDINGS_EVENT_LABELS,
  STANDINGS_EVENT_TYPES,
  type StandingsEvent,
  type StandingsEventType,
} from '@gl/shared';

/**
 * A league table over time, as one chart.
 *
 * Ten teams is past the point where distinct hues stay tellable apart, so
 * colour carries emphasis rather than identity: every line is recessive until a
 * team is hovered, and only then does colour say anything. Identity comes from
 * the names down either edge — on the left in the order the teams started, on
 * the right in the order they finished, so the shape of the season is readable
 * before anything is hovered at all.
 *
 * What moved a line is marked on it: a symbol replaces the dot in any week a
 * team traded, took someone off the wire, or was missing players.
 */

const ACCENT = '#a5d62e'; // volt — 11:1 on the panel, and the only saturated ink
// The unhighlighted lines carry the shape of the season, so they have to clear
// 3:1 against the panel the way any meaningful graphic does — at field-700 they
// were nearer 1.9:1 and the chart read as an empty box until something was
// hovered. This sits at 3.2:1: legible cold, still plainly behind the accent.
const CONTEXT = '#5b6675';
const GRID = '#1a1f27'; // field-800
const AXIS_TEXT = '#7d8796'; // 5.1:1 on the panel — small type, so it needs the room
const SURFACE = '#0e1116'; // the panel, for marker rings

/** Chart geometry the edge labels have to line up with, in pixels. */
const CHART_HEIGHT = 320;
const PLOT_TOP = 10; // the chart's own top margin
const PLOT_BOTTOM = 30; // the height recharts gives the x axis
const LABEL_LINE = 18;

export interface PositionSeries {
  id: string;
  label: string;
  sublabel?: string;
  points: Array<{ x: string | number; position: number }>;
}

interface Props {
  series: PositionSeries[];
  /** Bottom of the axis: position 1 is best and sits at the top. */
  numTeams: number;
  xLabel: string;
  /** Shown instead of the chart when there is nothing plotted yet. */
  empty?: string;
  /** The reader's own team, marked down both edges so it is findable at a glance. */
  mine?: string | null;
  /** Trades, pickups and injuries to mark on the lines, if the chart has any. */
  events?: StandingsEvent[];
}

/* ------------------------------------------------------------------ */
/* Event markers                                                       */
/* ------------------------------------------------------------------ */

/**
 * A shape per kind of event, plus one for a week that had more than one.
 *
 * They have to be tellable apart at six pixels on a grey line, so they differ
 * in silhouette rather than in detail: a diamond, a triangle, a cross and a
 * burst read differently even at that size, where four variations on a circle
 * would not.
 */
type MarkerKind = StandingsEventType | 'multiple';

function markerPath(kind: MarkerKind, cx: number, cy: number, r: number): string {
  switch (kind) {
    case 'trade':
      return `M${cx},${cy - r} L${cx + r},${cy} L${cx},${cy + r} L${cx - r},${cy} Z`;
    case 'waiver':
      return `M${cx},${cy - r} L${cx + r},${cy + r * 0.82} L${cx - r},${cy + r * 0.82} Z`;
    case 'injury': {
      const arm = r * 0.4;
      return (
        `M${cx - arm},${cy - r} L${cx + arm},${cy - r} L${cx + arm},${cy - arm} ` +
        `L${cx + r},${cy - arm} L${cx + r},${cy + arm} L${cx + arm},${cy + arm} ` +
        `L${cx + arm},${cy + r} L${cx - arm},${cy + r} L${cx - arm},${cy + arm} ` +
        `L${cx - r},${cy + arm} L${cx - r},${cy - arm} L${cx - arm},${cy - arm} Z`
      );
    }
    default: {
      // An eight-pointed burst: obviously "several things at once" rather than
      // a bigger version of any one of them.
      const points = Array.from({ length: 16 }, (_, index) => {
        const angle = (Math.PI / 8) * index - Math.PI / 2;
        const radius = index % 2 === 0 ? r : r * 0.42;
        return `${(cx + Math.cos(angle) * radius).toFixed(2)},${(cy + Math.sin(angle) * radius).toFixed(2)}`;
      });
      return `M${points.join(' L')} Z`;
    }
  }
}

/** Smallest and largest a marker is ever drawn, whatever it is worth. */
const MARKER_MIN = 3;
const MARKER_MAX = 9;

/**
 * One ordinary starter's season, in points per game, as the yardstick an injury
 * marker is measured against. Roughly a low-end flex: losing one of those for a
 * week draws the same symbol a single injury used to.
 */
const STARTER_PPG = 10;

const clamp = (value: number) => Math.min(Math.max(value, MARKER_MIN), MARKER_MAX);

/**
 * How big a marker is drawn.
 *
 * Trades and pickups are counted, because one move is one move. Injuries are
 * weighed instead: a week without a twenty-point running back is not the week a
 * third-string tight end pulled up, and counting names says they are. The
 * radius goes as the square root of the points, so it is the *area* of the
 * symbol that tracks what was lost — which is how a reader reads size — and it
 * is clamped at both ends, so nothing vanishes and nothing swallows its
 * neighbours.
 */
function markerRadius(kind: MarkerKind, weight: number): number {
  if (kind === 'injury') return clamp(3.4 * Math.sqrt(weight / STARTER_PPG));
  return clamp(3.4 + 1.3 * (weight - 1));
}

/** Teams a hover lists when none is being traced, before it says "and N more". */
const TOOLTIP_TEAMS = 4;

/**
 * The kind of marker a week's events call for, and how big it goes.
 *
 * A week of more than one kind is drawn at whatever its largest single event
 * would have been. The alternative is adding the weights together, but they are
 * not the same unit — two trades and eleven points of injury do not make
 * thirteen of anything.
 */
function markerFor(events: StandingsEvent[]): { kind: MarkerKind; radius: number } {
  const kind: MarkerKind = events.length > 1 ? 'multiple' : (events[0] as StandingsEvent).type;
  const radius = Math.max(...events.map((event) => markerRadius(event.type, event.weight)));
  return { kind, radius };
}

function MarkerGlyph({ kind, size = 13 }: { kind: MarkerKind; size?: number }) {
  const middle = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden className="shrink-0">
      <path d={markerPath(kind, middle, middle, middle - 1)} fill="currentColor" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */

function EventLines({ events }: { events: StandingsEvent[] }) {
  return (
    <>
      {events.map((event) => (
        <div key={event.type} className="mt-1.5">
          <p className="flex items-center gap-1 font-display text-[0.6875rem] uppercase tracking-bug text-brass-300">
            <MarkerGlyph kind={event.type} size={9} />
            {STANDINGS_EVENT_LABELS[event.type]} · {event.count}
            {/* The number the symbol was sized on, so a big cross explains
                itself without the reader adding up the names below it. */}
            {event.type === 'injury' && event.weight > 0
              ? ` · ${event.weight.toFixed(1)} ppg out`
              : ''}
          </p>
          <ul className="mt-0.5 space-y-0.5">
            {/* A hover is a glance, so a long week says how long it was rather
                than running off the bottom of the screen. */}
            {event.details.slice(0, 6).map((detail, index) => (
              <li key={index} className="text-xs leading-snug text-field-200">
                {detail}
              </li>
            ))}
            {event.details.length > 6 ? (
              <li className="text-xs text-field-500">+{event.details.length - 6} more</li>
            ) : null}
          </ul>
        </div>
      ))}
    </>
  );
}

function PositionTooltip({
  active,
  label,
  payload,
  xLabel,
  focus,
  series,
  marks,
}: {
  active?: boolean;
  payload?: Array<{ dataKey?: string | number; value?: number }>;
  label?: string | number;
  xLabel: string;
  focus: PositionSeries | undefined;
  series: PositionSeries[];
  marks: Map<string, Map<string | number, StandingsEvent[]>>;
}) {
  if (!active || label === undefined) return null;

  const own = focus ? payload?.find((entry) => entry.dataKey === focus.id) : undefined;
  const withEvents = series
    .map((team) => ({ team, events: marks.get(team.id)?.get(label) ?? [] }))
    .filter((entry) => entry.events.length > 0);

  // Nothing to say about this week for anybody: better no box than an empty one.
  if (!own?.value && withEvents.length === 0) return null;

  // With no team traced, a busy week belongs to all ten of them at once, which
  // is taller than the screen. Trace one and the box is that team's alone.
  const shown = withEvents.slice(0, TOOLTIP_TEAMS);

  return (
    <div className="max-w-xs border-2 border-brass-400 bg-field-950 px-2.5 py-1.5">
      <p className="font-display text-[0.6875rem] uppercase tracking-bug text-field-400">
        {xLabel} {label}
      </p>

      {focus && own?.value ? (
        <>
          <p className="font-display text-2xl font-bold leading-none text-brass-400">{own.value}</p>
          <EventLines events={marks.get(focus.id)?.get(label) ?? []} />
        </>
      ) : (
        <>
          {shown.map(({ team, events }) => (
            <div
              key={team.id}
              className="mt-1.5 border-t border-field-800 pt-1.5 first:border-0 first:pt-0"
            >
              <p className="font-display text-xs font-semibold uppercase tracking-tight text-field-50">
                {team.label}
              </p>
              <EventLines events={events} />
            </div>
          ))}
          {withEvents.length > shown.length ? (
            <p className="mt-2 border-t border-field-800 pt-1.5 text-xs text-field-500">
              {withEvents.length - shown.length} more team
              {withEvents.length - shown.length === 1 ? '' : 's'} this week — hover one to see it on
              its own.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

/** Where a team stood at one end of the chart, or null if it has no point there. */
function positionAt(team: PositionSeries, end: 'first' | 'last'): number | null {
  const point = end === 'first' ? team.points[0] : team.points[team.points.length - 1];
  return point?.position ?? null;
}

/** Teams in the order they stood at one end; anyone with no point there last. */
function orderedBy(series: PositionSeries[], end: 'first' | 'last'): PositionSeries[] {
  return [...series].sort((a, b) => {
    const left = positionAt(a, end);
    const right = positionAt(b, end);
    if (left === null || right === null) {
      return left === right ? 0 : left === null ? 1 : -1;
    }
    return left - right;
  });
}

/**
 * A column of team names beside the plot, one per position.
 *
 * The padding is what aligns them: with every position filled exactly once, a
 * column spread across the plot area puts each name on its own line of the
 * chart. Half a line comes off each end so the names sit centred on the
 * gridlines rather than hanging below them.
 */
function EdgeLabels({
  series,
  align,
  highlighted,
  mine,
  onHover,
  onPin,
}: {
  series: PositionSeries[];
  align: 'left' | 'right';
  highlighted: string | null;
  mine: string | null;
  onHover: (id: string | null) => void;
  onPin: (id: string) => void;
}) {
  return (
    <ul
      className="flex shrink-0 flex-col justify-between"
      style={{
        paddingTop: PLOT_TOP - LABEL_LINE / 2,
        paddingBottom: PLOT_BOTTOM - LABEL_LINE / 2,
      }}
    >
      {series.map((team, index) => {
        const on = team.id === highlighted;
        const own = team.id === mine;
        return (
          <li key={team.id} style={{ height: LABEL_LINE }}>
            <button
              type="button"
              onMouseEnter={() => onHover(team.id)}
              onMouseLeave={() => onHover(null)}
              onFocus={() => onHover(team.id)}
              onBlur={() => onHover(null)}
              // Clicking keeps a team highlighted on a touch screen, where
              // there is no hover to speak of.
              onClick={() => onPin(team.id)}
              className={`flex w-full items-center gap-1.5 px-1 font-display text-xs uppercase
                tracking-tight leading-[18px] transition-colors
                ${align === 'right' ? 'text-left' : 'flex-row-reverse text-right'}
                ${
                  on
                    ? 'bg-brass-400 font-bold text-field-950'
                    : own
                      ? 'font-bold text-field-50'
                      : 'font-medium text-field-400 hover:text-field-50'
                }`}
            >
              <span className="tabular-nums text-field-600">{index + 1}</span>
              <span className="max-w-[8rem] truncate">{team.label}</span>
              {/* Colour alone is the hover accent, so "yours" says so in words. */}
              {own ? (
                <span className="text-[0.625rem] uppercase tracking-bug text-brass-300">you</span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The toggles that decide which events are marked.
 *
 * Everything starts on: the point of the markers is that a reader who has not
 * asked for anything still sees what moved the season. Turning a type off is
 * how you go looking for one thing in particular.
 */
function EventFilters({
  counts,
  enabled,
  onToggle,
}: {
  counts: Record<StandingsEventType, number>;
  enabled: Set<StandingsEventType>;
  onToggle: (type: StandingsEventType) => void;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5">
      <span className="mr-0.5 font-display text-xs font-semibold uppercase tracking-bug text-field-400">
        Events
      </span>
      {STANDINGS_EVENT_TYPES.filter((type) => counts[type] > 0).map((type) => {
        const on = enabled.has(type);
        return (
          <button
            key={type}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(type)}
            className={`flex items-center gap-1.5 border px-2.5 py-0.5 font-display text-xs
              font-semibold uppercase tracking-bug transition-colors ${
                on
                  ? 'border-brass-400 bg-brass-400 text-field-950'
                  : 'border-field-700 bg-field-900 text-field-500 hover:text-field-100'
              }`}
          >
            <MarkerGlyph kind={type} size={11} />
            {STANDINGS_EVENT_LABELS[type]}
          </button>
        );
      })}
      <span className="ml-1 flex items-center gap-1.5 font-display text-[0.6875rem] uppercase tracking-bug text-field-500">
        <MarkerGlyph kind="multiple" size={11} />
        more than one
      </span>
    </div>
  );
}

export function HighlightedPositionChart({
  series,
  numTeams,
  xLabel,
  empty,
  mine = null,
  events = [],
}: Props) {
  const [active, setActive] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);
  const [hidden, setHidden] = useState<StandingsEventType[]>([]);
  const highlighted = active ?? pinned;

  const data = useMemo(() => {
    const rows = new Map<string | number, Record<string, string | number>>();
    for (const team of series) {
      for (const point of team.points) {
        const row = rows.get(point.x) ?? { x: point.x };
        row[team.id] = point.position;
        rows.set(point.x, row);
      }
    }
    return [...rows.values()];
  }, [series]);

  const started = useMemo(() => orderedBy(series, 'first'), [series]);
  const finished = useMemo(() => orderedBy(series, 'last'), [series]);

  const counts = useMemo(() => {
    const totals = { trade: 0, waiver: 0, injury: 0 } as Record<StandingsEventType, number>;
    for (const event of events) totals[event.type] += 1;
    return totals;
  }, [events]);

  const enabled = useMemo(
    () => new Set(STANDINGS_EVENT_TYPES.filter((type) => !hidden.includes(type))),
    [hidden],
  );

  /** The events on show, by team and then by the week they sit on. */
  const marks = useMemo(() => {
    const byTeam = new Map<string, Map<string | number, StandingsEvent[]>>();
    for (const event of events) {
      if (!enabled.has(event.type)) continue;
      const weeks = byTeam.get(event.teamId) ?? new Map<string | number, StandingsEvent[]>();
      const list = weeks.get(event.week) ?? [];
      list.push(event);
      weeks.set(event.week, list);
      byTeam.set(event.teamId, weeks);
    }
    return byTeam;
  }, [events, enabled]);

  if (series.length === 0 || data.length === 0) {
    return (
      <p className="border border-dashed border-field-700 px-5 py-8 text-center text-sm text-field-500">
        {empty ?? 'Nothing has been played yet.'}
      </p>
    );
  }

  const focus = series.find((team) => team.id === highlighted);
  const pin = (id: string) => setPinned((current) => (current === id ? null : id));
  const toggle = (type: StandingsEventType) =>
    setHidden((current) =>
      current.includes(type) ? current.filter((each) => each !== type) : [...current, type],
    );

  /**
   * The dot for one point of one team's line: the event symbol where something
   * happened that week, the plain marker where nothing did — and nothing at all
   * on the lines that are only there for context.
   */
  const dotFor = (teamId: string, focused: boolean) =>
    function EventDot(props: { cx?: number; cy?: number; payload?: { x?: string | number } }) {
      const { cx, cy } = props;
      if (cx === undefined || cy === undefined) return null;

      const week = props.payload?.x;
      const weekEvents = week === undefined ? undefined : marks.get(teamId)?.get(week);

      if (!weekEvents || weekEvents.length === 0) {
        if (!focused) return null;
        return <circle cx={cx} cy={cy} r={3.5} fill={ACCENT} stroke={SURFACE} strokeWidth={2} />;
      }

      const { kind, radius } = markerFor(weekEvents);
      return (
        <path
          d={markerPath(kind, cx, cy, radius)}
          fill={focused ? ACCENT : CONTEXT}
          stroke={SURFACE}
          strokeWidth={1.25}
        />
      );
    };

  return (
    <div className="card">
      {events.length > 0 ? (
        <EventFilters counts={counts} enabled={enabled} onToggle={toggle} />
      ) : null}

      <div className="table-scroll -mx-1 px-1">
        <div className="flex min-w-[36rem] items-stretch gap-1" style={{ height: CHART_HEIGHT }}>
          <EdgeLabels
            series={started}
            align="left"
            highlighted={highlighted ?? null}
            mine={mine}
            onHover={setActive}
            onPin={pin}
          />

          <div className="min-w-0 flex-1">
            <ResponsiveContainer width="100%" height="100%">
              {/*
                No gridlines: with one row per position and the names down both
                edges already marking them, horizontal rules only added ten
                straight lines behind the ones that carry the meaning.
              */}
              <LineChart data={data} margin={{ top: PLOT_TOP, right: 8, bottom: 0, left: 0 }}>
                <XAxis
                  dataKey="x"
                  height={PLOT_BOTTOM}
                  tick={{ fill: AXIS_TEXT, fontSize: 11, fontFamily: 'Barlow Condensed' }}
                  tickLine={false}
                  axisLine={{ stroke: GRID }}
                />
                <YAxis
                  // Position 1 is the best result, so the axis runs downwards.
                  reversed
                  domain={[1, numTeams]}
                  ticks={Array.from({ length: numTeams }, (_, index) => index + 1)}
                  tick={{ fill: AXIS_TEXT, fontSize: 11, fontFamily: 'Barlow Condensed' }}
                  tickLine={false}
                  axisLine={false}
                  width={22}
                />
                {focus || events.length > 0 ? (
                  <Tooltip
                    cursor={{ stroke: GRID }}
                    content={
                      <PositionTooltip
                        xLabel={xLabel}
                        focus={focus}
                        series={started}
                        marks={marks}
                      />
                    }
                  />
                ) : null}

                {series
                  .filter((team) => team.id !== highlighted)
                  .map((team) => (
                    <Line
                      key={team.id}
                      type="monotone"
                      dataKey={team.id}
                      stroke={CONTEXT}
                      strokeWidth={1.5}
                      dot={dotFor(team.id, false)}
                      activeDot={false}
                      isAnimationActive={false}
                      // Hovering the line itself is the other way in, alongside
                      // the names down either edge.
                      onMouseEnter={() => setActive(team.id)}
                      onMouseLeave={() => setActive(null)}
                      onClick={() => pin(team.id)}
                    />
                  ))}

                {/* Drawn last so it sits above the pack it is being compared with. */}
                {focus ? (
                  <Line
                    type="monotone"
                    dataKey={focus.id}
                    stroke={ACCENT}
                    strokeWidth={3}
                    strokeLinecap="butt"
                    strokeLinejoin="miter"
                    dot={dotFor(focus.id, true)}
                    activeDot={{ r: 5, fill: ACCENT, stroke: SURFACE, strokeWidth: 2 }}
                    isAnimationActive={false}
                  />
                ) : null}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <EdgeLabels
            series={finished}
            align="right"
            highlighted={highlighted ?? null}
            mine={mine}
            onHover={setActive}
            onPin={pin}
          />
        </div>
      </div>

      <p className="mt-3 border-l-2 border-field-700 pl-2 text-xs text-field-500">
        {focus
          ? `Showing ${focus.label}${focus.sublabel ? ` (${focus.sublabel})` : ''}. Hover another name or line to compare, or click to keep one in place.`
          : `Names on the left are the order the teams started, on the right the order they finished. Hover a name or a line to trace a team.`}
        {events.length > 0
          ? ' A symbol on a line marks a week something happened to that team; the bigger the symbol, the more of it. Hover the week for the details.'
          : ''}
      </p>
    </div>
  );
}
